// WebGL2 renderer: uploads decoded planes, converts them to R'G'B' in RGBA16F (C-07, C-09) and composites cells.

import { COMPOSITE_FS, FULLSCREEN_VS, RGBA16_COPY_FS, RGBA_COPY_FS, YUV_TO_RGB_FS } from './shaders';
import { yuvToRgbMatrix, type ColorSetup } from '../core/color';
import type { DecodedFrame } from '../media/protocol';
import type { Levels, ViewAdjustmentValues } from '../core/types';

export interface Xf {
  dx: number;
  dy: number;
  sx: number;
  sy: number;
}

export interface CellParams {
  rect: { x: number; y: number; w: number; h: number };
  mode: 'single' | 'wipe' | 'overlay' | 'difference';
  a: string | null;
  b: string | null;
  xfA: Xf;
  xfB: Xf;
  frameA: { w: number; h: number };
  viewOrigin: { x: number; y: number };
  viewScale: number;
  nearest: boolean;
  outside: [number, number, number];
  wipe: { pos: number; angleRad: number };
  opacity: number;
  /** band: 0 all, 1 low frequencies (light and colour), 2 high frequencies (detail), F-05.6/F-05.7. */
  diff: { gain: number; heat: boolean; luma: boolean; band?: 0 | 1 | 2 };
  channel: 0 | 1 | 2 | 3 | 4;
  texture: boolean;
  adjA: ViewAdjustmentValues | null;
  adjB: ViewAdjustmentValues | null;
  /** Clipping overlay (F-07.7); ref = A texture to compare against in single panes showing a B. */
  clip?: boolean;
  ref?: string | null;
  xfRef?: Xf;
}

interface SourceTexture {
  tex: WebGLTexture;
  fb: WebGLFramebuffer;
  w: number;
  h: number;
  key: string;
  proxy: boolean;
  /** Key of the frame whose mip chain was generated (frequency-split difference). */
  mipKey?: string;
}

type Uniforms = Record<string, WebGLUniformLocation | null>;

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error(`Shader compile failed: ${log}`);
  }
  return s;
}

function link(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`Program link failed: ${gl.getProgramInfoLog(p)}`);
  return p;
}

function uniforms(gl: WebGL2RenderingContext, p: WebGLProgram): Uniforms {
  const out: Uniforms = {};
  const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < n; i++) {
    const info = gl.getActiveUniform(p, i)!;
    const name = info.name.replace(/\[0\]$/, '');
    out[name] = gl.getUniformLocation(p, info.name);
  }
  return out;
}

const IDENTITY_LEVELS: Levels = { inBlack: 0, inWhite: 1, outBlack: 0, outWhite: 1, mid: 1 };

function levelsArray(adj: ViewAdjustmentValues | null): Float32Array {
  const out = new Float32Array(20);
  const put = (o: number, l: Levels) => {
    out[o] = l.inBlack;
    out[o + 1] = l.inWhite;
    out[o + 2] = l.outBlack;
    out[o + 3] = l.outWhite;
    out[o + 4] = l.mid;
  };
  put(0, adj?.levels ?? IDENTITY_LEVELS);
  put(5, adj?.perChannel?.r ?? IDENTITY_LEVELS);
  put(10, adj?.perChannel?.g ?? IDENTITY_LEVELS);
  put(15, adj?.perChannel?.b ?? IDENTITY_LEVELS);
  return out;
}

export class GLRenderer {
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  gl!: WebGL2RenderingContext;
  private progYuv!: WebGLProgram;
  private progRgba!: WebGLProgram;
  private progRgba16!: WebGLProgram;
  private progComp!: WebGLProgram;
  private uYuv!: Uniforms;
  private uRgba!: Uniforms;
  private uRgba16!: Uniforms;
  private uComp!: Uniforms;
  private vao!: WebGLVertexArrayObject;
  private planes: WebGLTexture[] = [];
  private rgbaIn!: WebGLTexture;
  private dummyTex!: WebGLTexture;
  private mipSampler!: WebGLSampler;
  private sources = new Map<string, SourceTexture>();
  lost = false;
  onRestored: (() => void) | null = null;
  readonly supportsFloat: boolean;

  constructor(canvas: HTMLCanvasElement | OffscreenCanvas) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      antialias: false,
      premultipliedAlpha: false,
      alpha: false,
      preserveDrawingBuffer: false,
      desynchronized: false,
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error('WebGL2 unavailable');
    this.gl = gl;
    this.supportsFloat = !!gl.getExtension('EXT_color_buffer_float');
    this.init();
    if ('addEventListener' in canvas) {
      (canvas as HTMLCanvasElement).addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        this.lost = true;
      });
      (canvas as HTMLCanvasElement).addEventListener('webglcontextrestored', () => {
        // E-20: rebuild GPU state; callers re-upload frames because all keys are cleared.
        this.lost = false;
        this.sources.clear();
        this.gl.getExtension('EXT_color_buffer_float');
        this.init();
        this.onRestored?.();
      });
    }
  }

  private init(): void {
    const gl = this.gl;
    this.progYuv = link(gl, FULLSCREEN_VS, YUV_TO_RGB_FS);
    this.progRgba = link(gl, FULLSCREEN_VS, RGBA_COPY_FS);
    this.progRgba16 = link(gl, FULLSCREEN_VS, RGBA16_COPY_FS);
    this.uRgba16 = uniforms(gl, this.progRgba16);
    this.progComp = link(gl, FULLSCREEN_VS, COMPOSITE_FS);
    this.uYuv = uniforms(gl, this.progYuv);
    this.uRgba = uniforms(gl, this.progRgba);
    this.uComp = uniforms(gl, this.progComp);
    this.vao = gl.createVertexArray()!;
    this.planes = [gl.createTexture()!, gl.createTexture()!, gl.createTexture()!];
    for (const t of this.planes) this.initTex(t, gl.NEAREST);
    this.rgbaIn = gl.createTexture()!;
    this.initTex(this.rgbaIn, gl.NEAREST);
    this.mipSampler = gl.createSampler()!;
    gl.samplerParameteri(this.mipSampler, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.samplerParameteri(this.mipSampler, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.samplerParameteri(this.mipSampler, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.samplerParameteri(this.mipSampler, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.dummyTex = gl.createTexture()!;
    this.initTex(this.dummyTex, gl.NEAREST);
    gl.bindTexture(gl.TEXTURE_2D, this.dummyTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, 1, 1, 0, gl.RGBA, gl.HALF_FLOAT, null);
  }

  private initTex(t: WebGLTexture, filter: number): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  /** Generates the mip chain of a source texture once per uploaded frame (frequency-split difference only). */
  private ensureMips(st: SourceTexture): void {
    if (st.mipKey === st.key) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, st.tex);
    gl.generateMipmap(gl.TEXTURE_2D);
    st.mipKey = st.key;
  }

  private target(id: string, w: number, h: number): SourceTexture {
    const gl = this.gl;
    let s = this.sources.get(id);
    if (s && (s.w !== w || s.h !== h)) {
      gl.deleteTexture(s.tex);
      gl.deleteFramebuffer(s.fb);
      s = undefined;
    }
    if (!s) {
      const tex = gl.createTexture()!;
      this.initTex(tex, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, this.supportsFloat ? gl.RGBA16F : gl.RGBA8, w, h, 0, gl.RGBA, this.supportsFloat ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE, null);
      const fb = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      s = { tex, fb, w, h, key: '', proxy: false };
      this.sources.set(id, s);
    }
    return s;
  }

  sourceKey(id: string): string {
    return this.sources.get(id)?.key ?? '';
  }

  isProxy(id: string): boolean {
    return this.sources.get(id)?.proxy ?? false;
  }

  hasSource(id: string): boolean {
    return !!this.sources.get(id)?.key;
  }

  sourceSize(id: string): { w: number; h: number } | null {
    const s = this.sources.get(id);
    return s ? { w: s.w, h: s.h } : null;
  }

  clearSource(id: string): void {
    const s = this.sources.get(id);
    if (s) s.key = '';
  }

  /** Uploads a decoded frame and converts it into the source's RGB texture. */
  uploadFrame(id: string, f: DecodedFrame, color: ColorSetup, key: string): void {
    if (this.lost) return;
    const gl = this.gl;
    const st = this.target(id, f.width, f.height);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    if (f.format === 'RGBA16') {
      // 16-bit R'G'B'A as an integer texture; the pass normalises it (exact to 16 bits in RGBA16F where it matters).
      const words = new Uint16Array(f.data, f.layout[0].offset);
      gl.bindTexture(gl.TEXTURE_2D, this.planes[0]);
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, f.layout[0].stride / 8);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16UI, f.width, f.height, 0, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, words);
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
      this.runPass(this.progRgba16, st, () => {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.planes[0]);
        gl.uniform1i(this.uRgba16.uTex, 0);
      });
      st.key = key;
      st.proxy = false;
      return;
    }
    if (f.format === 'RGBA') {
      const bytes = new Uint8Array(f.data);
      gl.bindTexture(gl.TEXTURE_2D, this.rgbaIn);
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, f.layout[0].stride / 4);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, f.width, f.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, bytes.subarray(f.layout[0].offset));
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
      this.runPass(this.progRgba, st, () => {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.rgbaIn);
        gl.uniform1i(this.uRgba.uTex, 0);
        gl.uniformMatrix4fv(this.uRgba.uCorrect, false, new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
      });
      st.key = key;
      st.proxy = false;
      return;
    }
    const high = f.format.includes('P1');
    const bpp = high ? 2 : 1;
    const nv12 = f.format === 'NV12';
    const sx = f.format.startsWith('I444') ? 0 : 1;
    const sy = f.format.startsWith('I420') || nv12 ? 1 : 0;
    const cw = (f.width + (1 << sx) - 1) >> sx;
    const ch = (f.height + (1 << sy) - 1) >> sy;
    const planeDims: [number, number, number][] = nv12
      ? [
          [f.width, f.height, 1],
          [cw, ch, 2],
        ]
      : [
          [f.width, f.height, 1],
          [cw, ch, 1],
          [cw, ch, 1],
        ];
    planeDims.forEach(([w, h, comps], i) => {
      const l = f.layout[i];
      gl.bindTexture(gl.TEXTURE_2D, this.planes[i]);
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, l.stride / (bpp * comps));
      const internal = high ? (comps === 2 ? gl.RG16UI : gl.R16UI) : comps === 2 ? gl.RG8UI : gl.R8UI;
      const fmt = comps === 2 ? gl.RG_INTEGER : gl.RED_INTEGER;
      if (high) {
        const arr = new Uint16Array(f.data, l.offset, (l.stride / 2) * (h - 1) + w * comps);
        gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, fmt, gl.UNSIGNED_SHORT, arr);
      } else {
        const arr = new Uint8Array(f.data, l.offset, l.stride * (h - 1) + w * comps);
        gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, fmt, gl.UNSIGNED_BYTE, arr);
      }
    });
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
    const bitDepth = high ? (f.format.includes('P12') ? 12 : 10) : 8;
    const m = yuvToRgbMatrix({ ...color, bitDepth });
    // mat4 column-major: column j = coefficient of input j (Y, Cb, Cr, 1) for outputs (r, g, b, _).
    const mat = new Float32Array([m[0], m[4], m[8], 0, m[1], m[5], m[9], 0, m[2], m[6], m[10], 0, m[3], m[7], m[11], 1]);
    this.runPass(this.progYuv, st, () => {
      const u = this.uYuv;
      for (let i = 0; i < 3; i++) {
        gl.activeTexture(gl.TEXTURE0 + i);
        gl.bindTexture(gl.TEXTURE_2D, this.planes[Math.min(i, planeDims.length - 1)]);
      }
      gl.uniform1i(u.uY, 0);
      gl.uniform1i(u.uU, 1);
      gl.uniform1i(u.uV, 2);
      gl.uniform1i(u.uLayout, nv12 ? 1 : 0);
      gl.uniform2i(u.uChromaShift, sx, sy);
      gl.uniform1f(u.uMaxCode, 2 ** bitDepth - 1);
      gl.uniformMatrix4fv(u.uMatrix, false, mat);
    });
    st.key = key;
    st.proxy = false;
  }

  /** Uploads a proxy bitmap (browser-converted, L1) as the source's RGB texture. */
  uploadBitmap(id: string, bmp: ImageBitmap, key: string): void {
    if (this.lost) return;
    const gl = this.gl;
    const st = this.target(id, bmp.width, bmp.height);
    gl.bindTexture(gl.TEXTURE_2D, this.rgbaIn);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
    this.runPass(this.progRgba, st, () => {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.rgbaIn);
      gl.uniform1i(this.uRgba.uTex, 0);
      gl.uniformMatrix4fv(this.uRgba.uCorrect, false, new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
    });
    st.key = key;
    st.proxy = true;
  }

  /**
   * GPU path (S-03): uploads a VideoFrame converted by the browser, then applies a row-major 4x4 correction so the
   * result matches our own conversion. Much faster than copying planes from hardware frames.
   */
  uploadVideoFrame(id: string, vf: VideoFrame, correction: number[], key: string): void {
    if (this.lost) return;
    const gl = this.gl;
    const w = vf.displayWidth;
    const h = vf.displayHeight;
    const st = this.target(id, w, h);
    gl.bindTexture(gl.TEXTURE_2D, this.rgbaIn);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, vf);
    const m = correction;
    const colMajor = new Float32Array([m[0], m[4], m[8], m[12], m[1], m[5], m[9], m[13], m[2], m[6], m[10], m[14], m[3], m[7], m[11], m[15]]);
    this.runPass(this.progRgba, st, () => {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.rgbaIn);
      gl.uniform1i(this.uRgba.uTex, 0);
      gl.uniformMatrix4fv(this.uRgba.uCorrect, false, colMajor);
    });
    st.key = key;
    st.proxy = false;
  }

  private runPass(prog: WebGLProgram, st: SourceTexture, bind: () => void): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, st.fb);
    gl.viewport(0, 0, st.w, st.h);
    gl.disable(gl.SCISSOR_TEST);
    gl.useProgram(prog);
    bind();
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /** Resizes the drawing buffer and clears it. */
  begin(width: number, height: number, bg: [number, number, number]): void {
    if (this.lost) return;
    if (this.canvas.width !== width) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, width, height);
    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(bg[0], bg[1], bg[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  drawCell(c: CellParams): void {
    if (this.lost) return;
    const gl = this.gl;
    const H = this.canvas.height;
    const r = c.rect;
    if (r.w <= 0 || r.h <= 0) return;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(r.x, H - r.y - r.h, r.w, r.h);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(r.x, H - r.y - r.h, r.w, r.h);
    gl.useProgram(this.progComp);
    const u = this.uComp;
    const sa = c.a ? this.sources.get(c.a) : undefined;
    const sb = c.b ? this.sources.get(c.b) : undefined;
    const hasA = !!(sa && sa.key);
    const hasB = !!(sb && sb.key);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, hasA ? sa!.tex : this.dummyTex);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, hasB ? sb!.tex : this.dummyTex);
    const sr = c.ref ? this.sources.get(c.ref) : undefined;
    const hasRef = !!(sr && sr.key);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, hasRef ? sr!.tex : this.dummyTex);
    gl.uniform1i(u.uTexA, 0);
    gl.uniform1i(u.uTexB, 1);
    gl.uniform1i(u.uTexRef, 3);
    gl.uniform1i(u.uClip, c.clip ? 1 : 0);
    gl.uniform1i(u.uHasRef, hasRef ? 1 : 0);
    gl.uniform2f(u.uSizeRef, sr?.w ?? 1, sr?.h ?? 1);
    const xfR = this.effectiveXf(c.xfRef ?? { dx: 0, dy: 0, sx: 1, sy: 1 }, sr, c.ref ?? null);
    gl.uniform4f(u.uXfRef, xfR.dx, xfR.dy, xfR.sx, xfR.sy);
    gl.uniform2f(u.uSizeA, sa?.w ?? 1, sa?.h ?? 1);
    gl.uniform2f(u.uSizeB, sb?.w ?? 1, sb?.h ?? 1);
    // Proxies are smaller than the source: scale the transform so A coords still land on the right texels.
    const xfA = this.effectiveXf(c.xfA, sa, c.a);
    const xfB = this.effectiveXf(c.xfB, sb, c.b);
    gl.uniform4f(u.uXfA, xfA.dx, xfA.dy, xfA.sx, xfA.sy);
    gl.uniform4f(u.uXfB, xfB.dx, xfB.dy, xfB.sx, xfB.sy);
    gl.uniform2f(u.uFrameA, c.frameA.w, c.frameA.h);
    gl.uniform2f(u.uViewOrigin, c.viewOrigin.x, c.viewOrigin.y);
    gl.uniform1f(u.uViewScale, c.viewScale);
    gl.uniform2f(u.uCellSize, r.w, r.h);
    gl.uniform1i(u.uMode, { single: 0, wipe: 1, overlay: 2, difference: 3 }[c.mode]);
    gl.uniform1i(u.uHasA, hasA ? 1 : 0);
    gl.uniform1i(u.uHasB, hasB ? 1 : 0);
    gl.uniform1i(u.uNearest, c.nearest ? 1 : 0);
    gl.uniform3f(u.uOutsideColor, ...c.outside);
    gl.uniform1f(u.uWipePos, c.wipe.pos);
    gl.uniform1f(u.uWipeAngle, c.wipe.angleRad);
    gl.uniform1f(u.uOpacity, c.opacity);
    gl.uniform1f(u.uDiffGain, c.diff.gain);
    gl.uniform1i(u.uDiffHeat, c.diff.heat ? 1 : 0);
    gl.uniform1i(u.uDiffLuma, c.diff.luma ? 1 : 0);
    const band = c.mode === 'difference' ? (c.diff.band ?? 0) : 0;
    gl.uniform1i(u.uDiffBand, band);
    // Frequency split: both sources are also bound through a mipmapping sampler; the blur is a mip level whose
    // texels cover about 1/120 of A's width (16 px for HD), the same area in A pixels for both sources.
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, hasA ? sa!.tex : this.dummyTex);
    gl.bindSampler(4, band ? this.mipSampler : null);
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, hasB ? sb!.tex : this.dummyTex);
    gl.bindSampler(5, band ? this.mipSampler : null);
    gl.uniform1i(u.uTexAMip, 4);
    gl.uniform1i(u.uTexBMip, 5);
    if (band) {
      if (hasA) this.ensureMips(sa!);
      if (hasB) this.ensureMips(sb!);
      gl.uniform1f(u.uBlurA, Math.max(2, c.frameA.w / 120));
    }
    gl.uniform1i(u.uChannel, c.channel);
    gl.uniform1i(u.uTexture, c.texture ? 1 : 0);
    gl.uniform1i(u.uAdjA, c.adjA ? 1 : 0);
    gl.uniform1i(u.uAdjB, c.adjB ? 1 : 0);
    gl.uniform4f(u.uAdjParamsA, c.adjA?.exposureStops ?? 0, c.adjA?.gamma ?? 1, 0, 0);
    gl.uniform4f(u.uAdjParamsB, c.adjB?.exposureStops ?? 0, c.adjB?.gamma ?? 1, 0, 0);
    gl.uniform1fv(u.uLevelsA, levelsArray(c.adjA));
    gl.uniform1fv(u.uLevelsB, levelsArray(c.adjB));
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.disable(gl.SCISSOR_TEST);
  }

  /** Native sizes are kept per source so proxies (smaller textures) map to the same A coordinates. */
  private nativeSizes = new Map<string, { w: number; h: number }>();

  setNativeSize(id: string, w: number, h: number): void {
    this.nativeSizes.set(id, { w, h });
  }

  private effectiveXf(xf: Xf, st: SourceTexture | undefined, id: string | null): Xf {
    if (!st || !id) return xf;
    const nat = this.nativeSizes.get(id);
    if (!nat || (nat.w === st.w && nat.h === st.h)) return xf;
    const kx = nat.w / st.w;
    const ky = nat.h / st.h;
    return { dx: xf.dx, dy: xf.dy, sx: xf.sx * kx, sy: xf.sy * ky };
  }

  /** Reads back RGBA8 pixels of the default framebuffer (top-left origin). Call right after drawing. */
  readPixels(x: number, y: number, w: number, h: number): Uint8Array {
    const gl = this.gl;
    const out = new Uint8Array(w * h * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.readPixels(x, this.canvas.height - y - h, w, h, gl.RGBA, gl.UNSIGNED_BYTE, out);
    // Flip rows to top-left origin.
    const row = w * 4;
    const tmp = new Uint8Array(row);
    for (let i = 0; i < h >> 1; i++) {
      const a = i * row;
      const b = (h - 1 - i) * row;
      tmp.set(out.subarray(a, a + row));
      out.copyWithin(a, b, b + row);
      out.set(tmp, b);
    }
    return out;
  }

  /** Reads the converted R'G'B' values (float) of a source texture region; used by tests (E-09). */
  readSourceRGB(id: string, x: number, y: number, w: number, h: number): Float32Array | null {
    const st = this.sources.get(id);
    if (!st) return null;
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, st.fb);
    const out = new Float32Array(w * h * 4);
    gl.readPixels(x, y, w, h, gl.RGBA, gl.FLOAT, out);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return out;
  }

  dispose(): void {
    const gl = this.gl;
    for (const s of this.sources.values()) {
      gl.deleteTexture(s.tex);
      gl.deleteFramebuffer(s.fb);
    }
    this.sources.clear();
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
